import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { pricesAPI, productImagesAPI, sizeOptionsAPI } from '../../services/api';
import ImageUpload from './ImageUpload';
import { X, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import './AddSizeDialog.css';

const AddSizeDialog = ({ isOpen, onClose, productId, onAddAnother }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    sizeOptionId: '',
    price: '',
    description: '',
    image: null,
  });
  const [shouldAddAnother, setShouldAddAnother] = useState(false);

  // Fetch size options for dropdown
  const { data: sizeOptionsData } = useQuery(
    'sizeOptions',
    async () => {
      const response = await sizeOptionsAPI.list();
      return response.data.data;
    },
    {
      enabled: isOpen,
    }
  );

  const sizeOptions = sizeOptionsData || [];

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        sizeOptionId: '',
        price: '',
        description: '',
        image: null,
      });
      setShouldAddAnother(false);
    }
  }, [isOpen]);

  const saveSizeMutation = useMutation(
    async () => {
      if (!formData.sizeOptionId || !formData.price) {
        throw new Error('Size and price are required');
      }

      // Create the price entry
      const priceData = {
        productId: productId,
        sizeOptionId: formData.sizeOptionId,
        price: parseFloat(formData.price),
        description: formData.description || '',
      };

      const response = await pricesAPI.create(priceData);
      const newPriceId = response.data.data.id;

      // Upload image if provided
      if (formData.image) {
        await productImagesAPI.upload(newPriceId, formData.image);
      }

      return newPriceId;
    },
    {
      onSuccess: () => {
        // Invalidate all relevant queries
        queryClient.invalidateQueries('prices');
        queryClient.invalidateQueries('products');
        queryClient.invalidateQueries(['product', productId]);
        queryClient.invalidateQueries('productImages');

        toast.success('Size added successfully');

        // Handle "Add Another" logic
        if (shouldAddAnother) {
          // Reset shouldAddAnother flag
          setShouldAddAnother(false);
          // Close and reopen to get a fresh form
          onClose();
          // Use setTimeout to ensure the dialog closes before reopening
          setTimeout(() => {
            if (onAddAnother) {
              onAddAnother();
            }
          }, 100);
        } else {
          // Just close
          onClose();
        }
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to add size');
      },
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageUpload = (file) => {
    setFormData((prev) => ({
      ...prev,
      image: file,
    }));
  };

  const handleImageDelete = () => {
    setFormData((prev) => ({
      ...prev,
      image: null,
    }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    setShouldAddAnother(false);
    saveSizeMutation.mutate();
  };

  const handleAddAnother = (e) => {
    e.preventDefault();
    setShouldAddAnother(true);
    saveSizeMutation.mutate();
  };

  if (!isOpen) return null;

  return (
    <div className="add-size-overlay" onClick={onClose}>
      <div className="add-size-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="add-size-header">
          <h2>Add Size</h2>
          <button className="close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form className="add-size-form" onSubmit={handleSave}>
          <div className="form-group">
            <label htmlFor="sizeOptionId">
              Size <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              id="sizeOptionId"
              name="sizeOptionId"
              value={formData.sizeOptionId}
              onChange={handleChange}
              required
              disabled={saveSizeMutation.isLoading}
            >
              <option value="">Select a size</option>
              {sizeOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="price">
              Price <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="number"
              id="price"
              name="price"
              value={formData.price}
              onChange={handleChange}
              step="0.01"
              min="0"
              required
              disabled={saveSizeMutation.isLoading}
              placeholder="0.00"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              disabled={saveSizeMutation.isLoading}
              placeholder="Optional size-specific details..."
              rows="4"
            />
          </div>

          <div className="form-group">
            <ImageUpload
              label="Product Image"
              currentImageUrl={null}
              onUpload={handleImageUpload}
              onDelete={handleImageDelete}
              isUploading={false}
              maxSize="512KB"
              dimensions="Recommended: 1000x1000px"
              acceptedFormats={['image/jpeg', 'image/png', 'image/webp']}
              maxSizeBytes={524288}
            />
          </div>

          <div className="add-size-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={onClose}
              disabled={saveSizeMutation.isLoading}
            >
              Cancel
            </button>
            <div className="left-actions">
              <button
                type="submit"
                className="save-button"
                disabled={saveSizeMutation.isLoading}
              >
                {saveSizeMutation.isLoading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                className="add-another-button"
                onClick={handleAddAnother}
                disabled={saveSizeMutation.isLoading}
              >
                <Plus size={16} />
                {saveSizeMutation.isLoading ? 'Saving...' : 'Add Another Size'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSizeDialog;
